import type {
  CreateVideoRequest,
  CreateVideoMetadata,
  UploadItem,
  DashboardUploaderS3Cfg,
} from '@/lib/types/Upload';
import type { IngestcfgData } from '@/lib/types/Ingest';
import { FilenameParser } from './FilenameParser';
import { CREDENTIAL_EXPIRATION_BUFFER_SECS } from '@/lib/constants/upload';

/** Aggregate state of the upload queue, for status banners. */
export type UploadQueuePhase = 'empty' | 'sending' | 'all-received' | 'attention';

export interface UploadQueueCounts {
  /** Client still responsible: pending / creating / uploading. */
  inFlight: number;
  processing: number;
  ready: number;
  failed: number;
  cancelled: number;
  total: number;
}

export class UploadUtils {
  /**
   * Build CreateVideoRequest from UploadItem + IngestcfgData.
   * Reference: lib2/model/media/CreateMediaReq.ts -> createVideoReqFromFilename()
   *
   * Published date priority chain (highest wins):
   *   filename-parsed date > ingestcfg.mediaSettings.published > file.lastModified
   */
  static buildCreateVideoRequest(
    item: UploadItem,
    ingestcfg: IngestcfgData
  ): CreateVideoRequest {
    const mediaSettings = ingestcfg.mediaSettings;
    const parsedMetadata = FilenameParser.parseMetadata(item.filename, item.lastModified ?? undefined);

    // Published priority chain: lastModified -> mediaSettings -> parser
    let published: number | null = item.lastModified
      ? Math.round(item.lastModified / 1000)
      : null;
    published = mediaSettings.published ?? published;
    published = parsedMetadata.published ?? published;

    const metadata: CreateVideoMetadata = {
      adSupported: !!mediaSettings.adSupported,
      availStart: mediaSettings.availStart || null,
      availEnd: mediaSettings.availEnd || null,
      published,
      refId: parsedMetadata.refId,
      tags: item.tags.length > 0 ? item.tags : ingestcfg.tags,
      title: parsedMetadata.title || item.title,
    };

    return {
      mediaStateType: 'active',
      ingestMethodType: 'dashboard',
      ingestcfgId: ingestcfg.ingestcfgId,
      mediaSourceType: 'uploaded_source',
      mediaType: 'video',
      srcFilename: item.filename,
      metadata,
    };
  }

  /**
   * Check if STS credentials are still valid (with 6hr buffer).
   * Returns false if credentials will expire within the buffer period.
   */
  static areCredentialsValid(cfg: DashboardUploaderS3Cfg): boolean {
    const nowSecs = Math.floor(Date.now() / 1000);
    return cfg.expirationTS - nowSecs > CREDENTIAL_EXPIRATION_BUFFER_SECS;
  }

  /**
   * Human-readable file size.
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = bytes / Math.pow(k, i);

    return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  }

  /** Per-status counts over the upload queue. */
  static getQueueCounts(items: readonly UploadItem[]): UploadQueueCounts {
    const counts: UploadQueueCounts = {
      inFlight: 0,
      processing: 0,
      ready: 0,
      failed: 0,
      cancelled: 0,
      total: items.length,
    };
    for (const item of items) {
      switch (item.status) {
        case 'pending':
        case 'creating':
        case 'uploading':
          counts.inFlight++;
          break;
        case 'processing':
          counts.processing++;
          break;
        case 'ready':
          counts.ready++;
          break;
        case 'error':
          counts.failed++;
          break;
        case 'cancelled':
          counts.cancelled++;
          break;
      }
    }
    return counts;
  }

  /**
   * Queue phase for status banners:
   * - 'sending': client is still responsible for bytes — leaving cancels uploads
   * - 'all-received': every file reached the server — leaving is safe
   * - 'attention': only failures (and cancels) remain
   * - 'empty': nothing to report (no items, or cancels only)
   */
  static getQueuePhase(items: readonly UploadItem[]): UploadQueuePhase {
    const counts = this.getQueueCounts(items);
    if (counts.inFlight > 0) return 'sending';
    if (counts.processing + counts.ready > 0) return 'all-received';
    if (counts.failed > 0) return 'attention';
    return 'empty';
  }

  /**
   * Derive MIME type from filename extension.
   * Falls back to 'application/octet-stream' for unknown types.
   */
  static getContentType(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();
    const mimeMap: Record<string, string> = {
      mp4: 'video/mp4',
      mov: 'video/quicktime',
      avi: 'video/x-msvideo',
      mkv: 'video/x-matroska',
      webm: 'video/webm',
      m4v: 'video/x-m4v',
      wmv: 'video/x-ms-wmv',
      flv: 'video/x-flv',
      mpg: 'video/mpeg',
      mpeg: 'video/mpeg',
    };
    return (ext && mimeMap[ext]) || 'application/octet-stream';
  }
}
